import { supabase } from '../supabase';
import { config } from '../config';
import { projectId, publicAnonKey } from '../config';
import type { PatientVerification } from '../types';

interface VerificationToken {
  token: string;
  expiresAt: string;
}

/**
 * Generate a new verification token for clinic QR code
 */
export const generateVerificationToken = async (clinicId: string): Promise<VerificationToken> => {
  // 🔧 DEVELOPMENT MODE - Return mock token
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Generating mock verification token for clinic:', clinicId);
    
    const mockToken: VerificationToken = {
      token: `VERIFY_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(), // 10 minutes
    };
    
    console.log('✅ Mock token generated:', { 
      token: mockToken.token.slice(0, 20) + '...', 
      expiresAt: mockToken.expiresAt 
    });
    
    return mockToken;
  }

  // Get current session
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError || !session) {
    throw new Error('Authentication required');
  }

  // Call backend to generate token
  const response = await fetch(
    `https://${projectId}.supabase.co/functions/v1/make-server-cc125661/verify/token`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        clinic_id: clinicId,
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to generate token' }));
    throw new Error(errorData.message || errorData.error || 'Failed to generate verification token');
  }

  const data = await response.json();
  return data;
};

/**
 * Get recent successful verifications for a clinic
 */
export const getRecentVerifications = async (
  clinicId: string,
  limit: number = 5
): Promise<any[]> => {
  // 🔧 DEVELOPMENT MODE - Return mock verifications
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Returning mock verifications for clinic:', clinicId);
    
    const mockVerifications = [
      {
        id: 'verif-1',
        user_id: '00000000-0000-0000-0000-000000000001',
        clinic_org_id: clinicId,
        verified: true,
        verified_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        verification_method: 'qr_scan',
        verified_by: '00000000-0000-0000-0000-000000000077',
        notes: 'Valid QR scan verification',
        created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'verif-2',
        user_id: '00000000-0000-0000-0000-000000000002',
        clinic_org_id: clinicId,
        verified: true,
        verified_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        verification_method: 'qr_scan',
        verified_by: '00000000-0000-0000-0000-000000000077',
        notes: 'QR scan completed',
        created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'verif-3',
        user_id: '00000000-0000-0000-0000-000000000003',
        clinic_org_id: clinicId,
        verified: true,
        verified_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        verification_method: 'qr_scan',
        verified_by: '00000000-0000-0000-0000-000000000077',
        notes: 'Patient verified via QR',
        created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      },
    ];
    
    console.log(`✅ Returning ${mockVerifications.length} mock verifications`);
    return mockVerifications.slice(0, limit);
  }

  // Get current session
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError || !session) {
    throw new Error('Authentication required');
  }

  // Call backend to get verification history
  const response = await fetch(
    `https://${projectId}.supabase.co/functions/v1/make-server-cc125661/verify/history/${clinicId}?limit=${limit}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to fetch verifications' }));
    throw new Error(errorData.message || errorData.error || 'Failed to fetch verification history');
  }

  const data = await response.json();
  return data.verifications || [];
};

/**
 * Verify a patient using QR token
 */
export const verifyPatientWithToken = async (
  token: string,
  userId: string
): Promise<{ success: boolean; message: string }> => {
  // 🔧 DEVELOPMENT MODE - Simulate verification
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Simulating patient verification with token');
    
    return {
      success: true,
      message: 'Patient verified successfully (mock)',
    };
  }

  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError || !session) {
    throw new Error('Authentication required');
  }

  // Call backend to verify token
  const response = await fetch(
    `https://${projectId}.supabase.co/functions/v1/make-server-cc125661/verify/patient`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        token,
        user_id: userId,
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Verification failed' }));
    throw new Error(errorData.message || errorData.error || 'Failed to verify patient');
  }

  const data = await response.json();
  return data;
};

/**
 * Exchange verification token for verified status (patient-facing)
 */
export const exchangeVerificationToken = async (
  token: string,
  userId: string
): Promise<{ success: boolean; message: string; clinic_id?: string }> => {
  // 🔧 DEVELOPMENT MODE - Simulate exchange
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Exchanging verification token');
    console.log('Token:', token.slice(0, 20) + '...');
    console.log('User ID:', userId);
    
    // Simulate delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    return {
      success: true,
      message: 'Verification successful (mock)',
      clinic_id: '00000000-0000-0000-0000-111111111111',
    };
  }

  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError || !session) {
    throw new Error('Authentication required');
  }

  // Call backend to exchange token
  const response = await fetch(
    `https://${projectId}.supabase.co/functions/v1/make-server-cc125661/verify/exchange`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        token,
        user_id: userId,
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Verification failed' }));
    throw new Error(errorData.message || errorData.error || 'Failed to exchange verification token');
  }

  const data = await response.json();
  return data;
};
